import React, { useEffect, useState, useMemo, useRef } from 'react';
import { groupCRMUsersByDTC, sortCRMUsersWithDTCPriority } from '../../utils/crmUserSorting';
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  Button,
  useDisclosure,
  HStack,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  useColorModeValue,
  Select as ChakraSelect,
  FormControl,
  FormLabel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  Badge,
  Spinner,
  Center,
  Icon,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Textarea,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';
import { FiSettings, FiPlus, FiDownload, FiCalendar, FiShield, FiUpload, FiSearch, FiX, FiInfo } from 'react-icons/fi';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
// Cloud Functions import removed - contacts now created directly in Supabase
import { Formik } from 'formik';
import * as Yup from 'yup';
import { InputControl, SelectControl, SubmitButton } from 'formik-chakra-ui';
import { db } from '../../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { CRM_VIEW_KEYS, readViewSearch, writeViewSearch } from '../../utils/crmViewSession';
import CommandPalette from '../../components/CRM/CommandPalette';
import BulkActionsBar from '../../components/CRM/BulkActionsBar';
import { BulkStageModal, BulkTagModal, BulkAssignModal, BulkDealRecordScopeModal, BulkDeleteModal } from '../../components/CRM/BulkModals';
import BulkPostcardModal from '../../components/CRM/BulkPostcardModal';
import BulkEnrollCampaignModal from './Workspace/BulkEnrollCampaignModal';
import UnifiedEventModal from '../../components/CRM/Calendar/UnifiedEventModal';
import TaskCompletionActivityPrompt, { taskHasLoggableEntity } from '../../components/CRM/TaskCompletionActivityPrompt';
import TaskDetailModal from '../../components/Tasks/TaskDetailModal';
import { useTasks } from '../../hooks/useTasks';
import { useCrmDefaultOwnerScope, CRM_OWNER_SCOPE } from '../../hooks/useCrmDefaultOwnerScope';
import { useDealsTable, applyDealScopeSync, searchContactIdsForTerm } from '../../hooks/useDealsTable';
import { useVisibilityScope } from '../../hooks/useVisibilityScope';
import { useCRMTablePreferences } from '../../hooks/useCRMTablePreferences';
import { useSmartLists } from '../../hooks/useSmartLists';
import usePostcardAccess from '../../hooks/usePostcardAccess';
import { useAgentPage } from '../../components/FutureBot/FutureBotProvider';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import { useMyDealsAgentTools } from './agent/useMyDealsAgentTools';
import { PRODUCT_TYPES, LEAD_SOURCES, LOAN_PURPOSES } from '../../constants/crmConstants';
import CustomizableDealsTable from '../../components/CRM/CustomizableDealsTable';
import TableCustomizationModal from '../../components/CRM/TableCustomizationModal';
import ImportModal from '../../components/CRM/Import/ImportModal';
import DealsFilterToolbar from '../../components/CRM/DealsFilterToolbar';
import { ALL_COLUMNS } from '../../components/CRM/tableColumnDefinitions';
import { ALL_CONTACTS_COLUMNS } from '../../components/CRM/contactsTableColumnDefinitions';
import NotificationPermissionBanner from '../../components/CRM/NotificationPermissionBanner';

import moment from 'moment';
import Papa from 'papaparse';

// Baseline filters applied by default (and restored when clearing a smart list selection)
const BASELINE_FILTERS = [
  { field: 'stage', operator: 'not_in', value: ['deal-lost'] }
];

// Stages that take a deal out of the active pipeline. Includes the legacy loan
// slugs so historical deals are classified the same way as current ones.
const CLOSED_STAGES = ['deal-won', 'deal-lost', 'loan-funded', 'loan-brokercheck-received', 'loan-finalized'];
const FUNDED_STAGES = ['deal-won', 'loan-funded', 'loan-brokercheck-received', 'loan-finalized'];

// PostgREST `not.in.(...)` list form
const CLOSED_STAGES_LIST = `(${CLOSED_STAGES.map(s => `"${s}"`).join(',')})`;

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  return {
    userId: params.get('userId'),
    search: params.get('search'),
    sort: params.get('sort'),
    sortDir: params.get('sortDir'),
    page: params.get('page'),
    pageSize: params.get('pageSize'),
    list: params.get('list'),
    filters: params.get('filters'),
  };
}

const addLeadValidationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email'),
  phone: Yup.string(),
  product: Yup.string(),
  source: Yup.string(),
  amount: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  propertyAddress: Yup.string(),
  purchaseOrRefi: Yup.string(),
  notes: Yup.string(),
}).test('email-or-phone', 'Either email or phone is required', function (value) {
  const { email, phone } = value;
  return email || phone;
});

function MyDealsPageContent({ defaultOwnerScope }) {
  const { currentUser, SuperAdmin, Roles, loading: authLoading } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { canSendPostcards } = usePostcardAccess();

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Parse URL params once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialParams = useMemo(() => parseUrlParams(location.search), []);

  // Ref for deferred reconciliation (smart lists load async)
  const urlSmartListRef = useRef(initialParams.list);

  // Filters arrive from the URL synchronously, so seed the table hook directly
  // rather than fetching with BASELINE_FILTERS and immediately refetching.
  const initialUrlFilters = useMemo(() => {
    if (initialParams.list) return null; // a smart list's filters load async
    if (!initialParams.filters) return null;
    try {
      const parsed = JSON.parse(decodeURIComponent(initialParams.filters));
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      console.warn('Failed to parse URL filters:', e);
      return null;
    }
  }, [initialParams.list, initialParams.filters]);

  // Any view state in the URL means this is a restored or shared view, which
  // takes precedence over the user's default smart list.
  const hasUrlViewState = Boolean(
    initialParams.list || initialParams.filters || initialParams.search ||
    initialParams.sort || initialParams.page
  );

  // State
  // An explicit ?userId= param always wins ('all' is the All Users sentinel);
  // otherwise admins open on the scope they chose in CRM Settings > Default View.
  const [selectedUserId, setSelectedUserId] = useState(() => {
    if (initialParams.userId) {
      return initialParams.userId === 'all' ? '' : initialParams.userId;
    }
    if (!isCRMAdmin) return '';
    return defaultOwnerScope === CRM_OWNER_SCOPE.ALL ? '' : (currentUser?.uid || '');
  });
  const [crmUsers, setCrmUsers] = useState([]);
  const [selectedTableDeals, setSelectedTableDeals] = useState([]);
  const [isAllDealsSelected, setIsAllDealsSelected] = useState(false);
  const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tableSearchTerm, setTableSearchTerm] = useState(initialParams.search || '');
  const [activeSearchTerm, setActiveSearchTerm] = useState(initialParams.search || '');
  const [isExporting, setIsExporting] = useState(false);
  const [exportColumnMode, setExportColumnMode] = useState('visible');

  // Search handler functions - now triggers server-side search
  const handleSearch = () => {
    setActiveSearchTerm(tableSearchTerm);
    setDealsGlobalSearch(tableSearchTerm);
  };

  const handleClearSearch = () => {
    setTableSearchTerm('');
    setActiveSearchTerm('');
    setDealsGlobalSearch('');
  };
  
  // Pagination state for scalability (Managed by useDeals now)
  // const [dealsLastDoc, setDealsLastDoc] = useState(null);
  // const [dealsHasMore, setDealsHasMore] = useState(false);
  // const DEALS_PAGE_SIZE = 50;
  
  // Disclosures
  const { isOpen: isAddLeadOpen, onOpen: onAddLeadOpen, onClose: onAddLeadClose } = useDisclosure();
  const { isOpen: isCommandPaletteOpen, onOpen: onCommandPaletteOpen, onClose: onCommandPaletteClose } = useDisclosure();
  const { isOpen: isBulkStageOpen, onOpen: onBulkStageOpen, onClose: onBulkStageClose } = useDisclosure();
  const { isOpen: isBulkTagOpen, onOpen: onBulkTagOpen, onClose: onBulkTagClose } = useDisclosure();
  const { isOpen: isBulkAssignOpen, onOpen: onBulkAssignOpen, onClose: onBulkAssignClose } = useDisclosure();
  const { isOpen: isBulkScopeOpen, onOpen: onBulkScopeOpen, onClose: onBulkScopeClose } = useDisclosure();
  const { isOpen: isBulkDeleteOpen, onOpen: onBulkDeleteOpen, onClose: onBulkDeleteClose } = useDisclosure();
  const { isOpen: isBulkPostcardOpen, onOpen: onBulkPostcardOpen, onClose: onBulkPostcardClose } = useDisclosure();
  const { isOpen: isBulkEnrollOpen, onOpen: onBulkEnrollOpen, onClose: onBulkEnrollClose } = useDisclosure();
  const { isOpen: isTaskModalOpen, onOpen: onTaskModalOpen, onClose: onTaskModalClose } = useDisclosure();
  const { isOpen: isTaskDetailOpen, onOpen: onTaskDetailOpen, onClose: onTaskDetailClose } = useDisclosure();
  const { isOpen: isActivityPromptOpen, onOpen: onActivityPromptOpen, onClose: onActivityPromptClose } = useDisclosure();
  const { isOpen: isTableCustomOpen, onOpen: onTableCustomOpen, onClose: onTableCustomClose } = useDisclosure();
  const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
  const { isOpen: isExportModalOpen, onOpen: onExportModalOpen, onClose: onExportModalClose } = useDisclosure();

  const isDTCLoanOfficer = Roles?.includes('DTC Loan Officer');

  // Single user filter for both deals and tasks
  // For CRM Admins: use selectedUserId if set, otherwise currentUser.uid for tasks
  // For regular users: always use currentUser.uid
  const effectiveUserId = isCRMAdmin ? (selectedUserId || currentUser?.uid) : currentUser?.uid;

  // True when the active filter is the admin's own data (no selection, or self selected).
  // Used so the personal "My Deals" framing is preserved when defaulted to self.
  const isViewingSelf = !selectedUserId || selectedUserId === currentUser?.uid;
  
  // Aggregated deal stats (for ALL deals, not just current page)
  // totalDeals/newLeads/atRiskDeals = active pipeline only (excludes won/lost)
  // wonDeals/lostDeals = historical counts for conversion rate calculation
  const [dealStats, setDealStats] = useState({
    totalDeals: 0,      // Active deals only
    newLeads: 0,        // Active new leads
    atRiskDeals: 0,     // Active at-risk deals
    wonDeals: 0,        // All-time won (for conversion rate)
    lostDeals: 0,       // All-time lost (for conversion rate)
  });

  // Tasks hook - filter by type and active statuses at database level, so the
  // RPC's row cap is spent entirely on tasks that can actually count toward
  // the "Due Today"/"Overdue" stats (and cancelled tasks never inflate them).
  const { tasks: crmTasks, updateTask, completeTask, createTask, reopenTask, deleteTask } = useTasks(effectiveUserId, { type: 'crm', status: ['pending', 'in-progress'] });
  const [completedTaskForPrompt, setCompletedTaskForPrompt] = useState(null);
  
  // Table preferences hook
  const { preferences, updatePreferences, resetToDefaults } = useCRMTablePreferences(currentUser?.uid);
  
  // Extract preferences
  const visibleColumns = preferences.visibleColumns || [];
  const columnOrder = preferences.columnOrder || [];
  const sortBy = preferences.sortBy || '';
  const sortDirection = preferences.sortDirection || 'asc';
  const columnFilters = preferences.filters || {};
  
  // Helper to safely get date from dueDate (handles both Firestore Timestamp and JS Date)
  const getDateFromDueDate = (dueDate) => {
    if (!dueDate) return null;
    return dueDate.toDate ? dueDate.toDate() : dueDate;
  };

  // Memoize expensive task calculations (only pending/in-progress tasks are
  // fetched, so completed/cancelled can never be counted here)
  const crmTasksDueToday = useMemo(() => {
    return crmTasks.filter(t => {
      if (!t.dueDate) return false;
      return moment(getDateFromDueDate(t.dueDate)).isSame(moment(), 'day');
    });
  }, [crmTasks]);

  const crmTasksOverdue = useMemo(() => {
    return crmTasks.filter(t => {
      if (!t.dueDate) return false;
      return moment(getDateFromDueDate(t.dueDate)).isBefore(moment(), 'day');
    });
  }, [crmTasks]);

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const boxBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBG = useColorModeValue('white', 'gray.800');

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // ⌘N / Ctrl+N - New lead
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        onAddLeadOpen();
      }
      // ⌘K / Ctrl+K - Command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onCommandPaletteOpen();
      }
      // ⌘F / Ctrl+F - Focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        document.querySelector('input[placeholder="Search..."]')?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onAddLeadOpen, onCommandPaletteOpen]);

  // Fetch CRM users — needed by all viewers (not just admins) so owner names
  // resolve on records shared via teams/book shares (multi-owner Phase 2).
  useEffect(() => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('CRMUser', '==', true));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          uid: doc.id,
          name: userData.Name || userData.name || userData.displayName || userData.Email || userData.email,
          email: userData.Email || userData.email,
          ...userData,
        });
      });
      setCrmUsers(sortCRMUsersWithDTCPriority(users));
    });

    return () => unsubscribe();
  }, []);

  // Create users lookup map for Owner column
  const usersMap = useMemo(() => {
    const map = new Map();
    crmUsers.forEach(user => {
      map.set(user.uid, user.name);
    });
    return map;
  }, [crmUsers]);

  // Multi-owner visibility scope for every deals query on this page (table,
  // stat cards, select-all, export). For CRM Admins: null target = All Users,
  // selectedUserId = that user's effective book. Regular users: own book.
  const { scope: dealsOwnerScope } = useVisibilityScope({
    entityType: 'deal',
    targetUserId: isCRMAdmin ? (selectedUserId || null) : currentUser?.uid,
  });

  // Server-side deals table hook
  const {
    deals: allDeals,
    totalCount: dealsTotalCount,
    totalPages: dealsTotalPages,
    currentPage: dealsCurrentPage,
    pageSize: dealsPageSize,
    loading,
    filters: activeFilters,
    sort: dealsSort,
    searchTerm: dealsSearchTerm,
    applyFilters: applyDealFilters,
    applySort: applyDealSort,
    setPage: setDealsPage,
    setPageSize: setDealsPageSize,
    setGlobalSearch: setDealsGlobalSearch,
    refresh: refreshDeals,
    fetchAllIds: fetchAllDealIds,
    fetchAllDeals,
  } = useDealsTable({
    // Multi-owner visibility scope: owned + team book-shares + record shares
    // (+ company records for admins). Admin with no user selected = All Users.
    ownerScope: dealsOwnerScope,
    pageSize: parseInt(initialParams.pageSize, 10) || 100,
    initialSort: {
      field: initialParams.sort || sortBy || 'updatedAt',
      direction: initialParams.sortDir || sortDirection || 'desc',
    },
    initialPage: parseInt(initialParams.page, 10) || 1,
    globalSearchTerm: initialParams.search || activeSearchTerm,
    // Fresh visit: hide lost deals (user can toggle off in the filter toolbar).
    // On a restored/shared view a missing `filters` param means the user really
    // did clear them, so the baseline must not creep back in.
    initialFilters: initialUrlFilters || (hasUrlViewState ? [] : BASELINE_FILTERS),
  });

  // Clear "all selected" state when filters or search changes
  useEffect(() => {
    if (isAllDealsSelected) {
      setSelectedTableDeals([]);
      setIsAllDealsSelected(false);
    }
  }, [activeFilters, activeSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Smart Lists hook for deals (Supabase-based)
  const {
    smartLists,
    personalLists,
    globalLists,
    defaultList,
    selectedListId: selectedSmartListId,
    setSelectedListId: setSelectedSmartListId,
    createSmartList,
    updateSmartList,
    deleteSmartList,
    duplicateSmartList,
    setAsDefault: setSmartListAsDefault,
    unsetDefault: unsetSmartListDefault,
    loading: smartListsLoading,
  } = useSmartLists({ userId: currentUser?.uid, entityType: 'deals' });

  // Reconcile the URL's smart list, else auto-apply the user's default (once).
  const hasAppliedDefaultRef = useRef(false);
  useEffect(() => {
    if (smartListsLoading || hasAppliedDefaultRef.current) return;
    hasAppliedDefaultRef.current = true;

    // A restored or shared view wins over the user's default smart list.
    if (urlSmartListRef.current) {
      const listId = urlSmartListRef.current;
      urlSmartListRef.current = null;
      const list = smartLists.find(l => l.id === listId);
      if (list) {
        setSelectedSmartListId(listId);
        applyDealFilters(list.filters || []);
      }
      return;
    }

    // Filters, search, sort and page from the URL were already seeded into
    // useDealsTable, so there is nothing left to reconcile here.
    if (hasUrlViewState) return;

    if (defaultList) {
      setSelectedSmartListId(defaultList.id);
      applyDealFilters(defaultList.filters || []);
      if (defaultList.sortBy) {
        applyDealSort(defaultList.sortBy, defaultList.sortDirection || 'desc');
        handleSortChange(defaultList.sortBy, defaultList.sortDirection || 'desc');
      }
      if (defaultList.visibleColumns && defaultList.visibleColumns.length > 0) {
        updatePreferences({
          visibleColumns: defaultList.visibleColumns,
          columnOrder: defaultList.visibleColumns,
        });
      }
    }
  }, [smartListsLoading, defaultList]); // eslint-disable-line react-hooks/exhaustive-deps

  // FutureBot: register this page's hands-on agent tools (filters, search,
  // sort, owner scope, smartlists). Must stay above any early return (hooks rule).
  const { registration: agentRegistration } = useMyDealsAgentTools({
    currentUser,
    isCRMAdmin,
    crmUsers,
    filters: activeFilters,
    applyFilters: applyDealFilters,
    baselineFilters: BASELINE_FILTERS,
    sort: dealsSort,
    applySort: applyDealSort,
    persistSort: (field, direction) => updatePreferences({ sortBy: field, sortDirection: direction }),
    applyVisibleColumns: (cols) => updatePreferences({ visibleColumns: cols, columnOrder: cols }),
    getVisibleColumns: () => visibleColumns,
    totalCount: dealsTotalCount,
    currentPage: dealsCurrentPage,
    pageSize: dealsPageSize,
    loading,
    activeSearchTerm,
    setTableSearchTerm,
    setActiveSearchTerm,
    setGlobalSearch: setDealsGlobalSearch,
    selectedUserId,
    setSelectedUserId,
    smartLists,
    selectedListId: selectedSmartListId,
    setSelectedListId: setSelectedSmartListId,
    createSmartList,
    updateSmartList,
    deleteSmartList,
    setAsDefault: setSmartListAsDefault,
  });
  useAgentPage(agentRegistration);

  // FeatureTour: named actions the My Deals tours can invoke (tourRegistry.js).
  // selectFirstDeal seeds a one-row selection so the bulk tour can spotlight
  // the bulk bar (it only renders while something is selected).
  useTourActions(useMemo(() => ({
    openAddLead: onAddLeadOpen,
    openImportForTour: onImportOpen,
    selectFirstDeal: () => {
      const first = allDeals[0];
      if (first) {
        setIsAllDealsSelected(false);
        setSelectedTableDeals([first.id]);
      }
    },
  }), [onAddLeadOpen, onImportOpen, allDeals]));

  // Sync the committed view to the URL via history.replace(), and remember it
  // for this tab so returning via the sidebar (which always navigates to a bare
  // /crm/my-deals) can replay it.
  useEffect(() => {
    // Smart lists load async. Writing before the URL's list has been reconciled
    // would strip it from both the URL and the saved snapshot.
    if (urlSmartListRef.current) return;

    const params = new URLSearchParams();

    // Admins: always record the owner choice explicitly. Omitting it for
    // "All Users" makes that selection unrecoverable, because the Default View
    // preference fills the gap on the next mount.
    if (isCRMAdmin) {
      params.set('userId', selectedUserId || 'all');
    }

    if (activeSearchTerm) {
      params.set('search', activeSearchTerm);
    }

    // Written unconditionally: the deals default sort comes from the Firestore
    // table preference rather than a fixed constant, so there is no stable value
    // to omit against without the restored view drifting when prefs change.
    params.set('sort', dealsSort?.field || sortBy || 'updatedAt');
    params.set('sortDir', dealsSort?.direction || sortDirection || 'desc');

    if (dealsCurrentPage > 1) {
      params.set('page', String(dealsCurrentPage));
    }

    if (dealsPageSize !== 100) {
      params.set('pageSize', String(dealsPageSize));
    }

    if (selectedSmartListId) {
      params.set('list', selectedSmartListId);
    }

    if (!selectedSmartListId && activeFilters && activeFilters.length > 0) {
      params.set('filters', encodeURIComponent(JSON.stringify(activeFilters)));
    }

    const search = params.toString();
    const newSearch = search ? `?${search}` : '';

    writeViewSearch(CRM_VIEW_KEYS.myDeals, newSearch);

    if (location.search !== newSearch) {
      history.replace({ pathname: location.pathname, search: newSearch });
    }
    // smartListsLoading is a dependency so this re-runs once the reconciliation
    // effect above (declared first, so it runs first) has cleared the ref -
    // including when the saved list no longer exists and no state changes.
  }, [selectedUserId, isCRMAdmin, activeSearchTerm, dealsSort, dealsCurrentPage, dealsPageSize, selectedSmartListId, activeFilters, sortBy, sortDirection, smartListsLoading, history, location.search, location.pathname]);

  // Fetch aggregated deal stats for ALL deals matching the CURRENT filters, not
  // just the current page. Shares applyDealScopeSync with the table so the cards
  // and the "All Deals" badge always describe the same set of deals.
  useEffect(() => {
    if (!isSupabaseConfigured() || !currentUser) return;

    let cancelled = false;

    const fetchDealStats = async () => {
      try {
        // Resolve search contact ids once and reuse across all five counts
        const contactIds = await searchContactIdsForTerm(activeSearchTerm);
        if (cancelled) return;

        // Counted server-side; no rows are transferred
        const countDeals = async (narrow, filtersForCount = activeFilters) => {
          let query = supabase.from('crm_deals').select('id', { count: 'exact', head: true });
          query = applyDealScopeSync(query, {
            ownerScope: dealsOwnerScope,
            searchTerm: activeSearchTerm,
            filters: filtersForCount,
            contactIds,
          });
          if (narrow) query = narrow(query);
          const { count, error } = await query;
          if (error) throw error;
          return count || 0;
        };

        // Won/lost are all-time counts whose stage is pinned by the narrow
        // below, so stage filters must not also apply to them. The baseline
        // "hide lost deals" filter otherwise contradicts the eq('stage',
        // 'deal-lost') narrow, forcing lostDeals to 0 and the conversion rate
        // to a permanent 100%.
        const nonStageFilters = (activeFilters || []).filter(f => f.field !== 'stage');

        const [totalDeals, newLeads, atRiskDeals, wonDeals, lostDeals] = await Promise.all([
          countDeals(q => q.not('stage', 'in', CLOSED_STAGES_LIST)),
          countDeals(q => q.eq('stage', 'lead-new')),
          countDeals(q => q.not('stage', 'in', CLOSED_STAGES_LIST).eq('is_at_risk', true)),
          countDeals(q => q.in('stage', FUNDED_STAGES), nonStageFilters),
          countDeals(q => q.eq('stage', 'deal-lost'), nonStageFilters),
        ]);

        if (cancelled) return;
        setDealStats({ totalDeals, newLeads, atRiskDeals, wonDeals, lostDeals });
      } catch (err) {
        if (!cancelled) console.error('Error calculating deal stats:', err);
      }
    };
    
    fetchDealStats();
    
    // Realtime stats refresh — same owner-channel strategy as useDealsTable:
    // one filtered channel per accessible owner (scoped), unfiltered for All
    // Users. Capped to avoid channel sprawl for very wide books.
    const scopedOwnerIds =
      dealsOwnerScope && dealsOwnerScope.mode === 'scoped'
        ? (dealsOwnerScope.ownerIds || [dealsOwnerScope.targetUid]).slice(0, 10)
        : null;
    
    let debounceTimer = null;
    const onStatsRowChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fetchDealStats, 500);
    };
    const statsSuffix = Math.random().toString(36).slice(2, 8);
    const statsChannels = [];
    if (scopedOwnerIds === null) {
      statsChannels.push(
        supabase
          .channel(`deals-stats-all-${statsSuffix}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_deals' }, onStatsRowChange)
          .subscribe()
      );
    } else {
      scopedOwnerIds.forEach((watchedOwnerId) => {
        statsChannels.push(
          supabase
            .channel(`deals-stats-${watchedOwnerId}-${statsSuffix}`)
            .on(
              'postgres_changes',
              { event: '*', schema: 'public', table: 'crm_deals', filter: `owner_id=eq.${watchedOwnerId}` },
              onStatsRowChange
            )
            .subscribe()
        );
      });
    }
    
    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      statsChannels.forEach((ch) => supabase.removeChannel(ch));
    };
  }, [currentUser, dealsOwnerScope, activeFilters, activeSearchTerm]);

  // Removed inline deal fetching useEffect
  /*
  // Fetch deals with pagination (SCALABILITY FIX)
  useEffect(() => {
    if (!currentUser) return;
    // ... (removed) ...
  }, [currentUser, isCRMAdmin, selectedUserId, effectiveUserId, DEALS_PAGE_SIZE]);
  */

  // Calculate conversion rate from closed deals (won / total closed)
  const conversionRate = useMemo(() => {
    const { wonDeals, lostDeals } = dealStats;
    const totalClosed = wonDeals + lostDeals;
    return totalClosed > 0 ? Math.round((wonDeals / totalClosed) * 100) : 0;
  }, [dealStats]);

  const handleDealClick = (deal) => {
    history.push(`/crm/deals/${deal.id}`);
  };

  const handleAddLead = async (values, actions) => {
    if (!isSupabaseConfigured()) {
      toast({
        title: 'Database not configured',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const assignTo = isCRMAdmin && values.assignTo ? values.assignTo : currentUser.uid;
      const isUnassigned = isCRMAdmin && !values.assignTo;
      const effectiveOwnerId = isUnassigned ? null : assignTo;
      
      // Use custom source if "other" is selected, otherwise use the selected source
      const effectiveSource = values.source === 'other' 
        ? (values.customSource || 'other') 
        : (values.source || 'manual');

      // Check for duplicates in Supabase (by email or phone for same owner)
      const emailToCheck = values.email?.trim().toLowerCase();
      const phoneToCheck = values.phone?.trim();

      if (emailToCheck) {
        const { data: existingByEmail } = await supabase
          .from('crm_contacts')
          .select('id, first_name, last_name, email')
          .ilike('email', emailToCheck)
          .eq('owner_id', effectiveOwnerId)
          .limit(1);

        if (existingByEmail && existingByEmail.length > 0) {
          const dup = existingByEmail[0];
          toast({
            title: 'Duplicate Lead',
            description: `Contact with this email already exists: ${dup.first_name} ${dup.last_name}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      if (phoneToCheck) {
        const { data: existingByPhone } = await supabase
          .from('crm_contacts')
          .select('id, first_name, last_name, phone')
          .eq('phone', phoneToCheck)
          .eq('owner_id', effectiveOwnerId)
          .limit(1);

        if (existingByPhone && existingByPhone.length > 0) {
          const dup = existingByPhone[0];
          toast({
            title: 'Duplicate Lead',
            description: `Contact with this phone already exists: ${dup.first_name} ${dup.last_name}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      // Create contact directly in Supabase (matches crm_contacts schema)
      const contactData = {
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        email: emailToCheck || null,
        phone: phoneToCheck || null,
        owner_id: effectiveOwnerId,
        contact_types: ['borrower'],
        tags: [],
        source: effectiveSource,
        is_active: true,
        is_favorite: false,
        do_not_contact: false,
      };

      const { data: newContact, error: contactError } = await supabase
        .from('crm_contacts')
        .insert(contactData)
        .select('id')
        .single();

      if (contactError) {
        throw new Error(`Failed to create contact: ${contactError.message}`);
      }

      // Proceed to create deal with the new contact's UUID
      await createDeal(values, actions, assignTo, isUnassigned, newContact.id);

    } catch (error) {
      console.error('Error adding lead:', error);
      toast({
        title: 'Error adding lead',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const createDeal = async (values, actions, assignTo, isUnassigned, contactId) => {
    if (!isSupabaseConfigured()) {
      toast({
        title: 'Database not configured',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      // Use custom source if "other" is selected, otherwise use the selected source
      const effectiveSource = values.source === 'other' 
        ? (values.customSource || 'other') 
        : (values.source || 'manual');

      // Insert deal into Supabase
      const { data, error } = await supabase
        .from('crm_deals')
        .insert({
          contact_id: contactId,
          deal_name: `${values.firstName} ${values.lastName}`,
          stage: 'lead-new',
          source_medium: effectiveSource,
          product: values.product || null,
          amount: values.amount ? parseFloat(values.amount) : 0,
          estimated_closing_date: null,
          next_action: 'follow-up-call',
          next_action_due: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          is_at_risk: false,
          arive_id: null,
          source_campaign: null,
          property_street: values.propertyAddress || null,
          purchase_or_refi: values.purchaseOrRefi || null,
          owner_id: isUnassigned ? null : assignTo,
        })
        .select('id')
        .single();

      if (error) throw error;

      if (values.notes && values.notes.trim()) {
        await supabase.from('crm_activities').insert({
          deal_id: data.id,
          type: 'note',
          content: values.notes.trim(),
          author_id: currentUser.uid,
          metadata: {
            activityType: 'created',
            disposition: '',
            authorName: currentUser.displayName || currentUser.email,
            authorPhotoURL: currentUser.photoURL || '',
          },
        });
      }

      const successMessage = isUnassigned 
        ? 'Lead added to unassigned list'
        : assignTo === currentUser.uid
        ? 'Lead added successfully'
        : 'Lead assigned successfully';

      toast({
        title: successMessage,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onAddLeadClose();
      actions.resetForm();
    } catch (error) {
      console.error('Error creating deal:', error);
      toast({
        title: 'Error creating deal',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };



  // After a linked task is completed, offer to log the interaction as an activity
  const promptActivityLogIfLinked = (task) => {
    if (taskHasLoggableEntity(task)) {
      setCompletedTaskForPrompt(task);
      onActivityPromptOpen();
    }
  };

  const handleCompleteTask = async (taskId) => {
    const task = crmTasks.find(t => t.id === taskId);
    if (task && task.status === 'completed') {
      // Reopen task
      await updateTask(taskId, { status: 'pending', completedAt: null });
    } else {
      const result = await completeTask(taskId);
      if (result?.success && task) promptActivityLogIfLinked(task);
    }
  };

  // TaskDetailModal shows its own completion toast, so this wrapper only completes + prompts
  const handleCompleteTaskFromDetail = async (taskId) => {
    const task = crmTasks.find(t => t.id === taskId);
    const result = await completeTask(taskId);
    if (result?.success && task) promptActivityLogIfLinked(task);
    return result;
  };

  const handleActivityPromptClose = () => {
    onActivityPromptClose();
    setCompletedTaskForPrompt(null);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    onExportModalClose();
    try {
      const exportDeals = await fetchAllDeals();
      const DEAL_DATE_COLUMNS = new Set(['due', 'created', 'lastTouched', 'appDate', 'estClosingDate']);
      const CONTACT_STATS_COLUMNS = new Set(['totalDeals', 'wonDeals', 'lifetimeValue', 'lastContact', 'score']);
      const CONTACT_DATE_COLUMNS = new Set(['createdAt']);

      const dealColumnsToExport = exportColumnMode === 'visible'
        ? visibleColumns
        : ALL_COLUMNS.map(c => c.id);

      const data = exportDeals.map(deal => {
        const row = {};

        dealColumnsToExport.forEach(colId => {
          const column = ALL_COLUMNS.find(c => c.id === colId);
          // Admin-only columns (record scope) never leave via non-admin exports
          if (!column || (column.adminOnly && !isCRMAdmin)) return;
          let value = column.accessor(deal);
          if (DEAL_DATE_COLUMNS.has(colId)) {
            value = value > 0 ? moment(value * 1000).format('YYYY-MM-DD') : '';
          } else if (colId === 'owner') {
            value = usersMap.get(value) || value || 'Unassigned';
          }
          row[column.label] = value;
        });

        if (exportColumnMode === 'allWithContact') {
          ALL_CONTACTS_COLUMNS.forEach(column => {
            if (CONTACT_STATS_COLUMNS.has(column.id)) return;
            if (column.adminOnly && !isCRMAdmin) return;
            const label = `Contact: ${column.label}`;
            let value = deal.contact ? column.accessor(deal.contact) : '';
            if (column.id === 'owner') {
              value = usersMap.get(value) || value || 'Unassigned';
            } else if (CONTACT_DATE_COLUMNS.has(column.id)) {
              value = value > 0 ? moment(value * 1000).format('YYYY-MM-DD') : '';
            }
            row[label] = value;
          });
        }

        return row;
      });

      const csv = Papa.unparse(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `crm-export-${moment().format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Export complete',
        description: `Exported ${data.length.toLocaleString()} deals to CSV`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error exporting deals:', error);
      toast({
        title: 'Export failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const toggleTableDealSelection = (dealId) => {
    // When user manually changes selection, exit "all selected" mode
    if (isAllDealsSelected) {
      setIsAllDealsSelected(false);
    }
    setSelectedTableDeals(prev =>
      prev.includes(dealId) ? prev.filter(id => id !== dealId) : [...prev, dealId]
    );
  };

  const toggleSelectAllTableDeals = () => {
    if (selectedTableDeals.length === allDeals.length) {
      setSelectedTableDeals([]);
    } else {
      setSelectedTableDeals(allDeals.map(d => d.id));
    }
  };
  
  // Handler for "Select All X deals" banner action
  const handleSelectAllMatchingFilters = async () => {
    setIsSelectAllLoading(true);
    try {
      const allIds = await fetchAllDealIds();
      setSelectedTableDeals(allIds);
      setIsAllDealsSelected(true);
      toast({
        title: `Selected ${allIds.length.toLocaleString()} deals`,
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error selecting all deals:', error);
      toast({
        title: 'Error selecting deals',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSelectAllLoading(false);
    }
  };
  
  // Handler for clearing all selections
  const handleClearAllSelection = () => {
    setSelectedTableDeals([]);
    setIsAllDealsSelected(false);
  };

  const handleBulkSuccess = () => {
    setSelectedTableDeals([]);
    setIsAllDealsSelected(false);
    refreshDeals();
  };

  // Contacts behind the selected deals (deduped) — campaign enrollment is contact-based
  const selectedDealContactIds = useMemo(() => {
    if (selectedTableDeals.length === 0) return [];
    const selected = new Set(selectedTableDeals);
    return [...new Set(
      allDeals
        .filter((d) => selected.has(d.id) && d.contactId)
        .map((d) => d.contactId)
    )];
  }, [selectedTableDeals, allDeals]);

  const handleSortChange = (columnId, direction) => {
    updatePreferences({
      sortBy: columnId,
      sortDirection: direction,
    });
  };

  const handleFilterChange = (columnId, filterValue) => {
    updatePreferences({
      filters: {
        ...columnFilters,
        [columnId]: filterValue,
      },
    });
  };

  const handleSaveTableCustomization = (updates) => {
    updatePreferences(updates);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    onTaskDetailOpen();
  };

  const handleEditTaskFromDetail = (task) => {
    setSelectedTask(null);
    onTaskDetailClose();
    toast({
      title: 'Edit task',
      description: 'Task editing coming soon',
      status: 'info',
      duration: 2000,
    });
  };

  const handleDeleteTaskFromDetail = async (taskId) => {
    const result = await deleteTask(taskId);
    if (result.success) {
      toast({
        title: 'Task deleted',
        status: 'success',
        duration: 2000,
      });
    }
    return result;
  };

  // Get user names for task detail modal
  const getTaskUserName = (userId) => {
    return crmUsers.find(u => u.uid === userId)?.name || 'Unknown User';
  };

  // Wait for both auth and deals to load
  if (authLoading || loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">{authLoading ? 'Authenticating...' : 'Loading your deals...'}</Text>
        </Center>
      </Container>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" overflowX="hidden">
      <Container maxW="1600px" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={6}>
          {/* Notification Permission Banner */}
          <NotificationPermissionBanner />
          
          {/* Header */}
          <Box bg={headerBG} p={{ base: 4, md: 6 }} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={3} data-tour="deals-header">
              <VStack align="flex-start" spacing={1}>
                <Heading size={{ base: 'md', md: 'lg' }}>
                  {isViewingSelf
                    ? '💼 My Deals'
                    : `💼 ${crmUsers.find(u => u.uid === selectedUserId)?.name || 'User'}'s Deals`}
                </Heading>
                <Text color="gray.600" fontSize="sm">
                  {isCRMAdmin && !selectedUserId
                    ? 'Viewing all deals & tasks across your team'
                    : !isViewingSelf
                    ? 'Viewing filtered deals & tasks'
                    : moment().format('dddd, MMMM DD, YYYY')}
                </Text>
              </VStack>
              <HStack spacing={2} flexWrap="wrap">
                {isCRMAdmin && (
                  <FormControl maxW={{ base: '100%', sm: '200px' }}>
                    <ChakraSelect
                      size="sm"
                      placeholder="All Users"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      {(() => {
                        const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                        return (
                          <>
                            {dtcUsers.length > 0 && (
                              <optgroup label="DTC Loan Officers">
                                {dtcUsers.map((user) => (
                                  <option key={user.uid} value={user.uid}>{user.name}</option>
                                ))}
                              </optgroup>
                            )}
                            <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                              {otherUsers.map((user) => (
                                <option key={user.uid} value={user.uid}>{user.name}</option>
                              ))}
                            </optgroup>
                          </>
                        );
                      })()}
                    </ChakraSelect>
                  </FormControl>
                )}
                <Tooltip label="CRM Settings">
                  <Button
                    leftIcon={<FiSettings />}
                    onClick={() => history.push('/crm/settings')}
                    variant="outline"
                    size="sm"
                  >
                    Settings
                  </Button>
                </Tooltip>
                
                {isCRMAdmin && (
                  <Tooltip label="CRM Admin Panel">
                    <Button
                      leftIcon={<FiShield />}
                      onClick={() => history.push('/crm/admin')}
                      variant="outline"
                      colorScheme="purple"
                      size="sm"
                    >
                      Admin Settings
                      <Badge ml={2} colorScheme="purple" variant="solid">
                        Admin
                      </Badge>
                    </Button>
                  </Tooltip>
                )}
                
                <Button
                  leftIcon={<FiCalendar />}
                  colorScheme="purple"
                  variant="outline"
                  onClick={onTaskModalOpen}
                  shadow="sm"
                  size="sm"
                  data-tour="deals-add-task"
                >
                  Add Task
                </Button>
                <Tooltip label="New Lead (⌘N)">
                  <Button
                    leftIcon={<FiPlus />}
                    colorScheme="blue"
                    onClick={onAddLeadOpen}
                    shadow="sm"
                    size="sm"
                    data-tour="deals-add-lead"
                  >
                    Add Lead
                  </Button>
                </Tooltip>
              </HStack>
            </HStack>

            {/* Quick Stats - Uses aggregated stats for ALL deals, not just current page */}
            <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }} gap={4} mt={6} data-tour="deals-stats">
              <GridItem>
                <Stat>
                  <HStack spacing={1}>
                    <StatLabel fontSize="xs">Total Pipeline</StatLabel>
                    <Tooltip
                      label="Open-pipeline deals matching your current view (user scope, search, and filters). Deals in Funded, Broker Check Received, Finalized, or Lost stages are always excluded — even if your filters include those stages."
                      fontSize="sm"
                      hasArrow
                      placement="top"
                    >
                      <Box as="span" display="inline-flex">
                        <Icon as={FiInfo} boxSize={3} color="gray.400" />
                      </Box>
                    </Tooltip>
                  </HStack>
                  <StatNumber>{dealStats.totalDeals}</StatNumber>
                  <StatHelpText>open deals</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Tasks Due Today</StatLabel>
                  <StatNumber color={crmTasksDueToday.length > 0 ? 'orange.500' : 'green.500'}>
                    {crmTasksDueToday.length}
                  </StatNumber>
                  <StatHelpText>
                    {crmTasksDueToday.length > 0 ? 'needs attention' : 'all clear'}
                  </StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Overdue Tasks</StatLabel>
                  <StatNumber color={crmTasksOverdue.length > 0 ? 'red.500' : 'green.500'}>
                    {crmTasksOverdue.length}
                  </StatNumber>
                  <StatHelpText>
                    {crmTasksOverdue.length > 0 ? 'take action now' : 'on track'}
                  </StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">At Risk</StatLabel>
                  <StatNumber color={dealStats.atRiskDeals > 0 ? 'red.500' : 'green.500'}>
                    {dealStats.atRiskDeals}
                  </StatNumber>
                  <StatHelpText>
                    {dealStats.atRiskDeals > 0 ? 'stagnant leads' : 'healthy'}
                  </StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Conversion Rate</StatLabel>
                  <StatNumber>{conversionRate}%</StatNumber>
                  <StatHelpText>
                    {dealStats.wonDeals} won
                  </StatHelpText>
                </Stat>
              </GridItem>
            </Grid>
          </Box>

          {/* Deals Table */}
          <Box bg={boxBG} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={6} shadow="sm" data-tour="deals-table">
              <VStack align="stretch" spacing={4}>
                {/* Table Header Row */}
                <HStack justify="space-between" flexWrap="wrap" gap={3}>
                  <HStack spacing={2}>
                    <Heading size="md">All Deals</Heading>
                    <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                      {dealsTotalCount.toLocaleString()}
                    </Badge>
                  </HStack>
                  <HStack spacing={2}>
                    {/* Search */}
                    <InputGroup size="sm" maxW="250px" data-tour="deals-search">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FiSearch} color="gray.400" boxSize={4} />
                      </InputLeftElement>
                      <Input
                        placeholder="Search name, email, phone, loan #, Arive ID..."
                        value={tableSearchTerm}
                        onChange={(e) => setTableSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        pr={tableSearchTerm ? '40px' : undefined}
                      />
                      {tableSearchTerm && (
                        <InputRightElement>
                          <IconButton
                            icon={<FiX />}
                            size="xs"
                            variant="ghost"
                            aria-label="Clear search"
                            onClick={handleClearSearch}
                          />
                        </InputRightElement>
                      )}
                    </InputGroup>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={handleSearch}
                    >
                      Search
                    </Button>
                    <Tooltip label="Customize table">
                      <IconButton
                        icon={<FiSettings />}
                        size="sm"
                        variant="outline"
                        onClick={onTableCustomOpen}
                        aria-label="Customize table"
                        data-tour="deals-customize"
                      />
                    </Tooltip>
                    <Button
                      size="sm"
                      leftIcon={<FiUpload />}
                      onClick={onImportOpen}
                      variant="outline"
                    >
                      Import
                    </Button>
                    {isCRMAdmin && (
                      <Button
                        size="sm"
                        leftIcon={<FiDownload />}
                        onClick={onExportModalOpen}
                        variant="outline"
                      >
                        Export
                      </Button>
                    )}
                  </HStack>
                </HStack>
                
                {/* Filter Toolbar */}
                <Box data-tour="deals-filters">
                <DealsFilterToolbar
                  filters={activeFilters}
                  onFiltersChange={applyDealFilters}
                  smartLists={smartLists}
                  personalLists={personalLists}
                  globalLists={globalLists}
                  selectedSmartListId={selectedSmartListId}
                  onSelectSmartList={(listId, listFilters, listSortBy, listSortDirection, listVisibleColumns) => {
                    setSelectedSmartListId(listId);
                    if (listId) {
                      // Applying a smart list: use its filters, sort, and columns
                      applyDealFilters(listFilters || []);
                      if (listSortBy) {
                        applyDealSort(listSortBy, listSortDirection || 'desc');
                        handleSortChange(listSortBy, listSortDirection || 'desc');
                      }
                      if (listVisibleColumns && listVisibleColumns.length > 0) {
                        updatePreferences({
                          visibleColumns: listVisibleColumns,
                          columnOrder: listVisibleColumns,
                        });
                      }
                    } else {
                      // Clearing selection: restore baseline filters
                      applyDealFilters(BASELINE_FILTERS);
                    }
                  }}
                  onCreateSmartList={createSmartList}
                  onUpdateSmartList={updateSmartList}
                  onDeleteSmartList={deleteSmartList}
                  onDuplicateSmartList={duplicateSmartList}
                  onSetDefaultSmartList={setSmartListAsDefault}
                  onUnsetDefaultSmartList={unsetSmartListDefault}
                  currentSort={{ field: dealsSort?.field || sortBy, direction: dealsSort?.direction || sortDirection }}
                  visibleColumns={visibleColumns}
                  loading={smartListsLoading}
                />
                </Box>
                
                {/* Deals Table */}
                <CustomizableDealsTable
                deals={allDeals}
                totalCount={dealsTotalCount}
                currentPage={dealsCurrentPage}
                totalPages={dealsTotalPages}
                pageSize={dealsPageSize}
                onPageChange={setDealsPage}
                onPageSizeChange={setDealsPageSize}
                sortBy={dealsSort?.field || sortBy}
                sortDirection={dealsSort?.direction || sortDirection}
                onSortChange={(field, direction) => {
                  applyDealSort(field, direction);
                  handleSortChange(field, direction);
                }}
                loading={loading}
                visibleColumns={visibleColumns}
                columnOrder={columnOrder}
                selectedDeals={selectedTableDeals}
                onSelectionChange={toggleTableDealSelection}
                onSelectAllMatchingFilters={handleSelectAllMatchingFilters}
                isAllMatchingFiltersSelected={isAllDealsSelected}
                isSelectAllLoading={isSelectAllLoading}
                onClearAllSelection={handleClearAllSelection}
                onDealClick={handleDealClick}
                usersMap={usersMap}
                ownerChipViewerId={isCRMAdmin ? null : currentUser?.uid}
              />
              {/* "Load More" button removed - using server-side pagination */}
              </VStack>
            </Box>
        </VStack>

        {/* Command Palette */}
        <CommandPalette
          isOpen={isCommandPaletteOpen}
          onClose={onCommandPaletteClose}
          selectedDeal={null}
          selectedContact={null}
        />

        {/* Bulk Actions Bar */}
        {selectedTableDeals.length > 0 && (
          <BulkActionsBar
            count={selectedTableDeals.length}
            onChangeStage={onBulkStageOpen}
            onAddTag={onBulkTagOpen}
            onAssign={onBulkAssignOpen}
            onDelete={onBulkDeleteOpen}
            onSendPostcard={canSendPostcards ? onBulkPostcardOpen : null}
            onEnrollCampaign={onBulkEnrollOpen}
            onClear={handleClearAllSelection}
            hasAssignedDeals={allDeals.filter(d => selectedTableDeals.includes(d.id)).some(d => d.ownerId)}
            isAllSelected={isAllDealsSelected}
            onSetRecordScope={isCRMAdmin ? onBulkScopeOpen : null}
          />
        )}

        {/* Bulk Operation Modals */}
        <BulkStageModal
          isOpen={isBulkStageOpen}
          onClose={onBulkStageClose}
          selectedDeals={selectedTableDeals}
          onSuccess={handleBulkSuccess}
          isDTCLoanOfficer={isDTCLoanOfficer}
        />

        <BulkTagModal
          isOpen={isBulkTagOpen}
          onClose={onBulkTagClose}
          selectedDeals={selectedTableDeals}
          onSuccess={handleBulkSuccess}
        />

        <BulkAssignModal
          isOpen={isBulkAssignOpen}
          onClose={onBulkAssignClose}
          selectedDeals={selectedTableDeals}
          selectedDealObjects={allDeals.filter(d => selectedTableDeals.includes(d.id))}
          crmUsers={crmUsers}
          onSuccess={handleBulkSuccess}
        />

        <BulkPostcardModal
          isOpen={isBulkPostcardOpen}
          onClose={onBulkPostcardClose}
          recipientType="deals"
          ids={selectedTableDeals}
          onSuccess={handleBulkSuccess}
        />

        <BulkEnrollCampaignModal
          isOpen={isBulkEnrollOpen}
          onClose={onBulkEnrollClose}
          selectedContacts={selectedDealContactIds}
          onSuccess={handleBulkSuccess}
        />

        <BulkDealRecordScopeModal
          isOpen={isBulkScopeOpen}
          onClose={onBulkScopeClose}
          selectedDeals={selectedTableDeals}
          onSuccess={handleBulkSuccess}
        />

        <BulkDeleteModal
          isOpen={isBulkDeleteOpen}
          onClose={onBulkDeleteClose}
          selectedDeals={selectedTableDeals}
          onSuccess={handleBulkSuccess}
        />

        {/* Task Modal */}
        <UnifiedEventModal
          isOpen={isTaskModalOpen}
          onClose={onTaskModalClose}
          onSave={onTaskModalClose}
          initialEventType="task"
        />

        {/* Task Detail Modal */}
        <TaskDetailModal
          isOpen={isTaskDetailOpen}
          onClose={onTaskDetailClose}
          task={selectedTask}
          onEdit={handleEditTaskFromDetail}
          onDelete={handleDeleteTaskFromDetail}
          onComplete={handleCompleteTaskFromDetail}
          onReopen={reopenTask}
          assignedUserName={selectedTask ? getTaskUserName(selectedTask.assignedTo) : null}
          createdByUserName={selectedTask ? getTaskUserName(selectedTask.createdBy) : null}
        />

        {/* Post-completion "log an activity?" prompt */}
        <TaskCompletionActivityPrompt
          isOpen={isActivityPromptOpen}
          onClose={handleActivityPromptClose}
          task={completedTaskForPrompt}
          onCreateTask={(taskData) => createTask(taskData)}
        />

        {/* Table Customization Modal */}
        <TableCustomizationModal
          isOpen={isTableCustomOpen}
          onClose={onTableCustomClose}
          visibleColumns={visibleColumns}
          columnOrder={columnOrder}
          onSave={handleSaveTableCustomization}
          onReset={resetToDefaults}
        />

        {/* Import CSV Modal */}
        <ImportModal
          isOpen={isImportOpen}
          onClose={onImportClose}
          importType="deals"
          autoAssignTo={currentUser?.uid}
          allowAssignmentSelection={isCRMAdmin}
          crmUsers={isCRMAdmin ? crmUsers : []}
          onImportComplete={() => {
            toast({
              title: 'Import complete',
              description: 'Deals imported successfully',
              status: 'success',
              duration: 3000,
            });
          }}
        />

        {/* Export Options Modal */}
        <Modal isOpen={isExportModalOpen} onClose={onExportModalClose} size="sm" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Export Deals</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.600" mb={4}>
                {dealsTotalCount.toLocaleString()} deals match your current filters. Choose which columns to include:
              </Text>
              <RadioGroup value={exportColumnMode} onChange={setExportColumnMode}>
                <VStack align="start" spacing={3}>
                  <Radio value="visible">
                    <Text fontSize="sm" fontWeight="medium">Visible columns only</Text>
                    <Text fontSize="xs" color="gray.500">{visibleColumns.length} columns currently shown in the table</Text>
                  </Radio>
                  <Radio value="all">
                    <Text fontSize="sm" fontWeight="medium">All deal columns</Text>
                    <Text fontSize="xs" color="gray.500">{ALL_COLUMNS.length} deal columns including hidden fields</Text>
                  </Radio>
                  <Radio value="allWithContact">
                    <Text fontSize="sm" fontWeight="medium">All deal + contact columns</Text>
                    <Text fontSize="xs" color="gray.500">{ALL_COLUMNS.length + ALL_CONTACTS_COLUMNS.length} columns from both tables</Text>
                  </Radio>
                </VStack>
              </RadioGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onExportModalClose} isDisabled={isExporting}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleExportCSV}
                isLoading={isExporting}
                loadingText="Exporting..."
                leftIcon={<FiDownload />}
              >
                Export CSV
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Add Lead Modal */}
        <Modal isOpen={isAddLeadOpen} onClose={onAddLeadClose} size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add New Lead</ModalHeader>
            <ModalCloseButton />
            <Formik
              initialValues={{
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                product: '',
                source: 'manual',
                customSource: '',
                assignTo: '',
                amount: '',
                propertyAddress: '',
                purchaseOrRefi: '',
                notes: '',
              }}
              validationSchema={addLeadValidationSchema}
              onSubmit={handleAddLead}
            >
              {({ handleSubmit, errors, values, setFieldValue }) => (
                <Box as="form" onSubmit={handleSubmit}>
                  <ModalBody>
                    <VStack spacing={4}>
                      <Box w="full" data-tour="lead-name">
                        <VStack spacing={4}>
                          <InputControl name="firstName" label="First Name" />
                          <InputControl name="lastName" label="Last Name" />
                        </VStack>
                      </Box>
                      <Box w="full" data-tour="lead-contact">
                        <VStack spacing={4}>
                          <InputControl name="email" label="Email" type="email" />
                          <InputControl name="phone" label="Phone" />
                        </VStack>
                      </Box>
                      
                      {isCRMAdmin && (
                        <Box w="full" data-tour="lead-assign">
                          <SelectControl name="assignTo" label="Assign To (Optional)">
                            <option value="">Leave Unassigned</option>
                            {(() => {
                              const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                              return (
                                <>
                                  {dtcUsers.length > 0 && (
                                    <optgroup label="DTC Loan Officers">
                                      {dtcUsers.map((user) => (
                                        <option key={user.uid} value={user.uid}>{user.name}</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                                    {otherUsers.map((user) => (
                                      <option key={user.uid} value={user.uid}>{user.name}</option>
                                    ))}
                                  </optgroup>
                                </>
                              );
                            })()}
                          </SelectControl>
                          <Text fontSize="xs" color="gray.600" w="full" mt={1}>
                            Leave blank to add to unassigned leads
                          </Text>
                        </Box>
                      )}
                      
                      <Divider />

                      <Box w="full" data-tour="lead-loan">
                        <VStack spacing={4}>
                      <SelectControl name="product" label="Product (Optional)">
                        <option value="">Select Product</option>
                        {PRODUCT_TYPES.map((product) => (
                          <option key={product.value} value={product.value}>
                            {product.label}
                          </option>
                        ))}
                      </SelectControl>
                      <InputControl name="amount" label="Loan Amount (Optional)" inputProps={{ type: 'number', placeholder: '250000' }} />
                      <SelectControl name="purchaseOrRefi" label="Purchase or Refi (Optional)">
                        <option value="">Select...</option>
                        {LOAN_PURPOSES.map((lp) => (
                          <option key={lp.value} value={lp.value}>
                            {lp.label}
                          </option>
                        ))}
                      </SelectControl>
                      <InputControl name="propertyAddress" label="Property Address (Optional)" inputProps={{ placeholder: '123 Main St, City, ST' }} />
                      <SelectControl name="source" label="Source">
                        <option value="manual">Manual Entry</option>
                        {LEAD_SOURCES.map((source) => (
                          <option key={source.value} value={source.value}>
                            {source.label}
                          </option>
                        ))}
                      </SelectControl>
                      {values.source === 'other' && (
                        <InputControl 
                          name="customSource" 
                          label="Custom Source" 
                          inputProps={{ placeholder: 'Enter custom source...' }}
                        />
                      )}
                        </VStack>
                      </Box>

                      <Divider />

                      <FormControl>
                        <FormLabel fontSize="sm">Notes (Optional)</FormLabel>
                        <Textarea
                          value={values.notes}
                          onChange={(e) => setFieldValue('notes', e.target.value)}
                          placeholder="Any important details about this lead..."
                          size="sm"
                          rows={3}
                        />
                      </FormControl>

                      {errors.email && errors.phone && (
                        <Text color="red.500" fontSize="sm">
                          Either email or phone is required
                        </Text>
                      )}
                    </VStack>
                  </ModalBody>
                  <ModalFooter data-tour="lead-submit">
                    <Button variant="ghost" mr={3} onClick={onAddLeadClose}>
                      Cancel
                    </Button>
                    <SubmitButton colorScheme="blue">Add Lead</SubmitButton>
                  </ModalFooter>
                </Box>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
}

// CrmSettingsProvider is at the app root (App.jsx) — no need to wrap again here
export default function MyDealsPage() {
  const location = useLocation();
  const { currentUser, SuperAdmin, Roles, loading: authLoading } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Resolve the admin's default scope BEFORE mounting the page, so the deals
  // query and realtime channel open once with the right owner instead of firing
  // for the wrong owner and immediately re-firing.
  const { defaultOwnerScope, loading: viewPrefLoading } = useCrmDefaultOwnerScope(
    isCRMAdmin ? currentUser?.uid : null
  );

  // Restore this tab's last view, but only on a bare list URL. A query string
  // means a deep link or shared URL, which must win.
  const savedSearch = useMemo(
    () => (location.search ? null : readViewSearch(CRM_VIEW_KEYS.myDeals)),
    [location.search]
  );

  // Fixing the URL before the content mounts lets its existing parse-on-mount
  // logic do all the rehydration, and keeps the deals query to a single fetch.
  // Safe to do before the auth gate below: the restored URL states the owner
  // explicitly, so this decision never reads a half-resolved auth state.
  if (savedSearch) {
    return <Redirect to={{ pathname: location.pathname, search: savedSearch }} />;
  }

  // Auth must resolve first: MyDealsPageContent picks its initial owner scope in
  // a useState initializer, which runs once on first render. Reading a
  // half-resolved auth state there decides SuperAdmin is false and silently
  // ignores the Default View setting, so whether an admin lands on "All Users"
  // or on themselves would come down to whether auth was cached.
  if (authLoading || viewPrefLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">Loading your deals...</Text>
        </Center>
      </Container>
    );
  }

  return <MyDealsPageContent defaultOwnerScope={defaultOwnerScope} />;
}
